import {
  connect,
  LocalParticipant,
  LocalTrack,
  LocalTrackPublication,
  Logger,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room
} from "twilio-video";
import {$KTM, BaseListItem, BaseListItemsLoader, KeyMetadatas, ListItemChange, OnListItemsListener} from "@/shared/types";
import {JsonObject} from "@/shared/json/json-object";
import {JsonProperty} from "@/shared/json/json-property";
import {Error, PhoneOutlined, VideoCallOutlined} from "@mui/icons-material";
import {JSON_OBJECT} from "@/shared/json/helpers";
import {md5_uuid} from "@/shared/md5";
import {getMemberAuth} from "@/shared/auth";
import {SvgIcon} from "@mui/material";
import {BaseApp} from "@/shared/BaseApp";
import {MessagesContextTrait} from "../types";
import {ChatMessage, ChatMessages, ParticipantKey, ParticipantType} from "../chat/types";

export const TOKEN_URL = "https://superfyne-apps-calltoken-service.web.app/generate";

export enum CallChannel {
  AUDIO = "audio",
  VIDEO = "video",
}

export type CallChannelMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const CALL_CHANNEL_METADATAS_KTM = new KeyMetadatas<CallChannelMetadata>([
  $KTM(CallChannel.AUDIO, {text: "Audio call", iconType: PhoneOutlined}),
  $KTM(CallChannel.VIDEO, {text: "Video call", iconType: VideoCallOutlined}),
])

export enum CallType {
  NONE = "none",
  CREATE = "create",
  CONNECT = "connect",
  START = "start",
  END = "end",
}

export enum CallEndReason {
  NO_ANSWER,
  HANG_UP,
  TIME_OUT,
  BUSY,
  NAVIGATED_AWAY,
  NO_PERMISSIONS,
}

@JsonObject()
export class Call extends BaseListItem {

  @JsonProperty()
  type: CallType;
  @JsonProperty()
  channel: CallChannel;
  @JsonProperty()
  time: number; // Last updated.
  started?: number;
  @JsonProperty()
  ended?: number;
  @JsonProperty()
  endReason?: CallEndReason;

  static createNew(channel: CallChannel) {
    const call = new Call(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), channel);
    call.type = CallType.CREATE;
    call.time = call.created;
    return call;
  }

  static updateConnect(call: Call): Call {
    const copy = new Call(call.id, call.creator, call.created, call.channel);
    copy.type = CallType.CONNECT;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateStart(call: Call): Call {
    const copy = new Call(call.id, call.creator, call.created, call.channel);
    copy.type = CallType.START;
    copy.started = Date.now();
    copy.time = copy.started;
    return copy;
  }

  static updateEnd(call: Call, endReason: CallEndReason): Call {
    const copy = new Call(call.id, call.creator, call.created, call.channel);
    copy.type = CallType.END;
    copy.ended = Date.now();
    copy.time = copy.ended;
    copy.endReason = endReason;
    return copy;
  }

  constructor(id: string, creator: string, created: number, channel: CallChannel) {
    super(id, creator, created);
    this.channel = channel;
  }
}

export const CALL_END_REASON_TEXT_FROM_ME = new Map<CallEndReason, string>([
    [
      CallEndReason.NO_ANSWER, "Did not answer"
    ],
    [
      CallEndReason.HANG_UP, "Call ended"
    ],
    [
      CallEndReason.TIME_OUT, "Lost connection"
    ],
    [
      CallEndReason.BUSY, "On another call",
    ],
    [
      CallEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      CallEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

export const CALL_END_REASON_TEXT_FROM_OTHER = new Map<CallEndReason, string>([
    [
      CallEndReason.NO_ANSWER, "You missed the call"
    ],
    [
      CallEndReason.HANG_UP, "Call ended"
    ],
    [
      CallEndReason.TIME_OUT, "Lost connection"
    ],
    [
      CallEndReason.BUSY, "On another call",
    ],
    [
      CallEndReason.NAVIGATED_AWAY, "Left",
    ],
    [
      CallEndReason.NO_PERMISSIONS, "Permissions not granted",
    ],
  ]
)

const IS_MOBILE = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

const DEFAULT_CONNECT_OPTIONS = {
  // Comment this line if you are playing music.
  maxAudioBitrate: 16000,

  // Capture 720p video @ 24 fps.
  video: {height: 720, frameRate: 24, width: 720}
};


@JsonObject()
export class RoomJoin extends BaseListItem {

  static createNew(roomId: string): RoomJoin {
    return new RoomJoin(roomId, getMemberAuth().getMemberId(), Date.now());
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export class RoomJoins extends BaseListItemsLoader<RoomJoin> {

  private static instance: RoomJoins;

  static getInstance(): RoomJoins {
    if (!this.instance) {
      this.instance = new RoomJoins();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "room_joins";
  }

  protected deserializeItem(value: any): RoomJoin {
    return JSON_OBJECT.deserializeObject(value, RoomJoin);
  }

  protected serializeItem(item: RoomJoin): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: RoomJoin, item2: RoomJoin): number {
    return item2.created - item1.created;
  }
}

export interface RoomListener {

  onConnected(room: Room);

  onAttachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDetachTrack(room: Room, track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant);

  onDisconnected(room: Room);
}

export interface CurrentCallListener {
  onCallChanged(currentCall: Call | null);
}

export class Calls extends BaseListItemsLoader<Call> implements OnListItemsListener<Call> {

  private static readonly instances = new Map<string, Calls>();

  private currentCall: Call;
  private currentCallListener: CurrentCallListener;
  private roomListener: RoomListener;

  static getInstance(roomId: string, listener: CurrentCallListener): Calls {
    let calls = this.instances.get(roomId);
    if (!calls) {
      calls = new Calls(roomId);
      this.instances.set(roomId, calls);
    }
    calls.currentCallListener = listener;
    return calls;
  }

  private constructor(readonly roomId: string) {
    super({shared: true});
    this.registerObserver(this);
  }

  getCurrentCall(): Call | null | undefined {
    return this.currentCall;
  }

  protected basePath(): string {
    return "calls/" + this.roomId;
  }

  protected async onAfterItemsLoaded(items: Call[]): Promise<Call[]> {
    for (const call of items) {
      if (!this.currentCall && call.type !== CallType.END) {
        this.currentCall = call;
      } else {
        if (call.type !== CallType.END) {
          await this.endOtherCall(call, CallEndReason.NAVIGATED_AWAY);
        }
        await this.deleteListItemById(call.id);
      }
    }
    return items;
  }

  onItemChanged(item: Call, change: ListItemChange): void {
    if (item.id === this.currentCall?.id) {
      this.updateCurrentCall(item);
    }
  }

  private updateCurrentCall(call: Call) {
    this.currentCall = call;
    this.currentCallListener?.onCallChanged(call);
    if (this.currentCall?.type === CallType.END) {
      this.room?.disconnect();
      this.room = null;
      switch (this.currentCall.endReason) {
        case CallEndReason.HANG_UP:
          setTimeout(() => {
            this.clearCurrentCall();
          }, 1000);
          break;
        case CallEndReason.NO_ANSWER:
        case CallEndReason.TIME_OUT:
        case CallEndReason.BUSY:
        case CallEndReason.NAVIGATED_AWAY:
        case CallEndReason.NO_PERMISSIONS:
          // Nothing. UI should present a button to clear out current call.
          break;
        default:
          this.clearCurrentCall();
          break;
      }
    }
  }

  protected deserializeItem(value: any): Call {
    return JSON_OBJECT.deserializeObject(value, Call);
  }

  protected serializeItem(item: Call): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Call, item2: Call): number {
    return item2.created - item1.created;
  }

  clearCurrentCall() {
    this.currentCall = null;
    this.currentCallListener?.onCallChanged(null);
  }

  // CALLER ONLY
  async createCall(channel: CallChannel, roomListener: RoomListener) {
    if (this.currentCall) {
      throw Error;
    }
    const createCall = Call.createNew(channel);
    this.currentCall = createCall;
    await this.addListItem(createCall);
    await this.setupAndJoinRoom(roomListener);
    // this.currenCallTimeoutId = setTimeout(() => this.endCurrentCall(CallEndReason.NO_ANSWER), 10 * 60000);
  }

  // CALLEE ONLY
  async connectAndStartCurrentCall(roomListener: RoomListener) {
    if (!this.currentCall) {
      throw Error;
    }
    const connectCall = Call.updateConnect(this.currentCall);
    this.currentCall = connectCall;
    await this.addListItem(connectCall);

    await this.setupAndJoinRoom(roomListener);

    const startCall = Call.updateStart(this.currentCall);
    this.currentCall = startCall;
    await this.addListItem(startCall);
  }

  private async setupAndJoinRoom(roomListener: RoomListener) {
    const response = await fetch(TOKEN_URL + `?identity=${getMemberAuth().getMemberId()}&room=${this.roomId}`);
    const token = await response.text();
    const deviceIds = {
      audio: IS_MOBILE ? null : localStorage.getItem('audioDeviceId'),
      video: IS_MOBILE ? null : localStorage.getItem('videoDeviceId')
    };
    const connectOptions: any = {
      ...DEFAULT_CONNECT_OPTIONS,
    }
    connectOptions.audio = {deviceId: {exact: deviceIds.audio}};
    connectOptions.name = this.roomId;
    connectOptions.video.deviceId = {exact: deviceIds.video};
    await this.joinRoom(token, connectOptions, roomListener);
  }

// CALLER OR CALLEE
  async endCurrentCall(reason: CallEndReason) {
    if (!this.currentCall) {
      throw Error;
    }
    const endCall = Call.updateEnd(this.currentCall, reason);
    this.currentCall = endCall;
    await this.addListItem(endCall);
  }

  private async endOtherCall(otherCall: Call, reason: CallEndReason) {
    const endCall = Call.updateEnd(otherCall, reason);
    this.currentCall = endCall;
    await this.addListItem(endCall);
  }

  private attachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.roomListener.onAttachTrack(this.room, track, participant);
  }

  private detachTrack(track: LocalTrack | RemoteTrack, participant: LocalParticipant | RemoteParticipant) {
    this.roomListener.onDetachTrack(this.room, track, participant);
  }

  private participantConnected(participant: LocalParticipant | RemoteParticipant) {
    // Handle the TrackPublications already published by the Participant.
    participant.tracks.forEach(publication => {
      this.trackPublished(publication, participant);
    });

    // Handle theTrackPublications that will be published by the Participant later.
    participant.on('trackPublished', publication => {
      this.trackPublished(publication, participant);
    });
  }

  private participantDisconnected(participant: LocalParticipant | RemoteParticipant) {
  }

  private trackPublished(publication: LocalTrackPublication | RemoteTrackPublication, participant: LocalParticipant | RemoteParticipant) {
    // If the TrackPublication is already subscribed to, then attach the Track to the DOM.
    if (publication.track) {
      this.attachTrack(publication.track, participant);
    }

    // Once the TrackPublication is subscribed to, attach the Track to the DOM.
    publication.on('subscribed', track => {
      this.attachTrack(track, participant);
    });

    // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
    publication.on('unsubscribed', track => {
      this.detachTrack(track, participant);
    });
  }

  private room: Room | null;

  private async joinRoom(token: string, connectOptions: any, listener: RoomListener) {
    this.roomListener = listener;
    // Comment the next two lines to disable verbose logging.
    const logger = Logger.getLogger('twilio-video');
    logger.setLevel('error');

    // Join to the Room with the given AccessToken and ConnectOptions.
    const room = await connect(token, connectOptions);
    this.room = room;
    this.roomListener.onConnected(this.room);

    // Save the LocalVideoTrack.
    const localVideoTrack = Array.from(room.localParticipant.videoTracks.values())[0].track;

    // Make the Room available in the JavaScript console for debugging.
    // window.room = room;

    // Handle the LocalParticipant's media.
    this.participantConnected(room.localParticipant);

    // Subscribe to the media published by RemoteParticipants already in the Room.
    room.participants.forEach(participant => {
      this.participantConnected(participant);
    });

    // Subscribe to the media published by RemoteParticipants joining the Room later.
    room.on('participantConnected', participant => {
      this.participantConnected(participant);
    });

    // Handle a disconnected RemoteParticipant.
    room.on('participantDisconnected', participant => {
      this.participantDisconnected(participant);
    });

    room.once('disconnected', (room, error) => {
      // Stop the LocalVideoTrack.
      localVideoTrack.stop();

      // Handle the disconnected LocalParticipant.
      this.participantDisconnected(room.localParticipant);

      // Handle the disconnected RemoteParticipants.
      room.participants.forEach(participant => {
        this.participantDisconnected(participant);
      });

      this.roomListener.onDisconnected(this.room);
    });
  }

  getOtherParticipantCount(): number {
    return this.room?.participants.size || 0;
  }
}

@JsonObject()
export class CallLog extends BaseListItem {

  message: ChatMessage;

  @JsonProperty()
  readonly threadId: string;

  @JsonProperty()
  readonly messageId: string;

  @JsonProperty()
  type: CallType = CallType.NONE;

  @JsonProperty()
  channel: CallChannel = CallChannel.AUDIO;

  static createNew(threadId: string, messageId: string): CallLog {
    return new CallLog(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), threadId, messageId)
  }

  constructor(id: string, creator: string, created: number, threadId: string, messageId: string) {
    super(id, creator, created);
    this.threadId = threadId;
    this.messageId = messageId;
  }
}

export class CallLogs extends BaseListItemsLoader<CallLog> {

  private static readonly instances = new Map<string, CallLogs>();

  static getInstance(type: ParticipantType) {
    let instance = this.instances.get(type);
    if (!instance) {
      instance = new CallLogs(BaseApp.CONTEXT.getTrait<MessagesContextTrait>().getMyParticipantKey(type));
      this.instances.set(type, instance);
    }
    return instance;
  }

  private constructor(readonly myParticipantKey: ParticipantKey) {
    super({shared: true /* each participant maintains own logs, but using participant key (see basePath) */, scopeRoot: true});
  }

  protected basePath(): string {
    // We don't store this under 'chats/threads/<threadId>/messages' to avoid loading all messages when a thread is loaded.
    return `callLogs/${this.myParticipantKey.id}`;
  }

  protected deserializeItem(value: any): CallLog {
    return JSON_OBJECT.deserializeObject(value, CallLog);
  }

  protected serializeItem(item: CallLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: CallLog, item2: CallLog): number {
    return item2.createdAt - item1.createdAt;
  }

  protected async onAfterItemDeserialized(item: CallLog, val?: any): Promise<CallLog> {
    const log = await super.onAfterItemDeserialized(item, val);
    log.message = await ChatMessages.getInstance(this.myParticipantKey.type, log.threadId).getOrLoadItem(log.messageId);
    return log;
  }
}